import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Divider,
  Drawer,
  Typography,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { SvgIcon } from "@mui/material";
import Bars3Icon from "@heroicons/react/24/solid/Bars3Icon";
import { salesItems } from "./sales-nav-items";
import { SideNavItem } from "../side-nav-item";
import { useLocation } from "react-router-dom";

const SalesSideNav = (props) => {
  const { pathname } = useLocation();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const { open, onClose } = props;

  const scrollContainerRef = useRef(null);
  const activeItemRef = useRef(null);

  const scrollbarStyles = {
    overflowY: "auto",
    flexGrow: 1,
    px: 2,
    py: 3,
    msOverflowStyle: "none",
    scrollbarWidth: "thin",
    "&::-webkit-scrollbar": {
      width: "12px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#555",
      borderRadius: "10px",
      border: "3px solid #f1f1f1",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#333",
    },
  };

  useEffect(() => {
    if (activeItemRef.current && scrollContainerRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [pathname]);

  useEffect(() => {
    const scrollableElement = scrollContainerRef.current;
    const lastScrollPos = localStorage.getItem("salesSideNavScrollPos") || 0;

    if (scrollableElement) {
      scrollableElement.scrollTop = lastScrollPos;

      const handleScroll = () => {
        localStorage.setItem(
          "salesSideNavScrollPos",
          scrollableElement.scrollTop
        );
      };

      scrollableElement.addEventListener("scroll", handleScroll);

      return () => {
        scrollableElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  const renderNavItems = (items) => (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        "& > *": {
          listStyle: "none",
        },
      }}
    >
      {items.map((item) => {
        const active = pathname === item.path;
        return (
          <SideNavItem
            key={item.title}
            ref={active ? activeItemRef : null}
            active={active}
            disabled={item.disabled}
            icon={item.icon}
            path={item.path}
            title={item.title}
          />
        );
      })}
    </Box>
  );

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ p: 3, pb: 1, position: "relative" }}>
        {/* Close button - only show on mobile (sm and below) */}
        {!lgUp && (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "common.white",
              zIndex: 1,
            }}
          >
            <SvgIcon fontSize="small">
              <Bars3Icon />
            </SvgIcon>
          </IconButton>
        )}
        <Box
          sx={{
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            display: "flex",
            textDecoration: "none",
            borderRadius: "27px",
          }}
        >
          <div>
            <img
              style={{
                width: "7rem",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "-3px",
                marginBottom: "-13px",
              }}
              alt="Permit Rockstar Logo"
              src="/img/website_img/PermitRockstar.png"
            />
          </div>
        </Box>
        <Typography
          sx={{ fontStyle: "italic" }}
          align="center"
          variant="h6"
          mt={1}
        >
          Sales Portal
        </Typography>
      </Box>
      <Divider sx={{ borderColor: "neutral.700" }} />
      <Box component="nav" ref={scrollContainerRef} sx={scrollbarStyles}>
        {renderNavItems(salesItems)}
      </Box>
      <Typography
        color="neutral.500"
        sx={{ textAlign: "center", pb: 2 }}
        variant="body2"
      >
        2023, All Rights Reserved
      </Typography>
    </Box>
  );

  const [forceHideSideNav, setForceHideSideNav] = useState(false);
  useEffect(() => {
    const handleForceOn = () => setForceHideSideNav(true);
    const handleForceOff = () => setForceHideSideNav(false);
    window.addEventListener("forceHideSideNavOn", handleForceOn);
    window.addEventListener("forceHideSideNavOff", handleForceOff);
    return () => {
      window.removeEventListener("forceHideSideNavOn", handleForceOn);
      window.removeEventListener("forceHideSideNavOff", handleForceOff);
    };
  }, []);

  if (lgUp && !forceHideSideNav) {
    return (
      <Drawer
        anchor="left"
        open
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "neutral.800",
              color: "common.white",
              width: props?.SIDE_NAV_WIDTH,
            },
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  if (forceHideSideNav) return <></>;

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "neutral.800",
            color: "common.white",
            width: {
              xs: "100%",
              sm: props?.SIDE_NAV_WIDTH,
            },
          },
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

export default SalesSideNav;
